import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import {
  JBMTableActionDef,
  JBMTableColumnAlignment,
  JBMTableColumnDef,
  JBMTableColumnSortEvent,
  JBMTableColumnType
} from '../JBMTable.component';

@Component({
  selector: 'jbm-table-header',
  templateUrl: './JBMTableHeader.component.html',
  styleUrls: ['./JBMTableHeader.component.scss']
})
export class JBMTableHeaderComponent implements OnInit {
  readonly ColType = JBMTableColumnType;
  configurableColumns: boolean = false

  @Input() columnDefs: JBMTableColumnDef[] = [];
  @Input() actionDefs: JBMTableActionDef[] = [];
  @Input() actionsColumnWidth: number=5;
  @Input() sortable: boolean = false;
  @Input() sortDirection: string = '+';
  @Input() hideHeader: boolean = false;

  @Output() sortChange = new EventEmitter<JBMTableColumnSortEvent>();
  @Output() columnDefsChange = new EventEmitter<JBMTableColumnDef[]>();

  // Drag & drop state
  dragSourceIndex: number | null = null;
  dragOverIndex: number | null = null;
  private dragging: boolean = false;

  // Resize state
  private resizing: boolean = false;
  private resizeSourceIndex: number | null = null;
  private resizeStartX: number = 0;
  private resizeStartWidth: number = 0;

  ngOnInit(): void {
    if (!this.columnDefs) this.columnDefs = [];
    if (!this.actionDefs) this.actionDefs = [];
    this.configurableColumns = this.columnDefs.some((cd: any) => cd.sizeable) || this.columnDefs.some((cd: any) => cd.moveable);
  }

  // Sort
  onSortClick(c: JBMTableColumnDef) {
    if (this.dragging || this.resizing) return;
    if (!this.sortable || !c.sortable) return;
    const descending = c.sorted ? this.sortDirection === '+' : false;
    this.sortChange.emit({ name: c.name, descending });
  }

  getTextAlign(c: JBMTableColumnDef): string {
    if (c.align === JBMTableColumnAlignment.left) return 'left';
    if (c.align === JBMTableColumnAlignment.right) return 'right';
    if (c.align === JBMTableColumnAlignment.center) return 'center';

    switch (c.type) {
      case JBMTableColumnType.number: return 'right';
      case JBMTableColumnType.price: return 'right';
      case JBMTableColumnType.boolean: return 'center';
      default: return 'left';
    }
  }

  // Drag & drop
  onDragStart(index: number) {
    this.dragSourceIndex = index;
    this.dragging = true;
  }
  onDragOver(event: DragEvent, index: number) {
    event.preventDefault();
    this.dragOverIndex = index;
  }
  onDrop(targetIndex: number) {
    if (this.dragSourceIndex === null || this.dragSourceIndex === targetIndex) {
      this.resetDragState();
      return;
    }
    const updated = [...this.columnDefs];
    const [moved] = updated.splice(this.dragSourceIndex, 1);
    updated.splice(targetIndex, 0, moved);
    this.columnDefsChange.emit(updated);
    this.resetDragState();
  }
  onDragEnd() {
    this.resetDragState();
  }

  private resetDragState() {
    this.dragSourceIndex = null;
    this.dragOverIndex = null;
    setTimeout(() => this.dragging = false);
  }

  // Resize
  onResizeStart(event: MouseEvent, index: number) {
    event.preventDefault();
    event.stopPropagation();
    const th = (event.target as HTMLElement).closest('th');
    this.resizeSourceIndex = index;
    this.resizeStartX = event.clientX;
    this.resizeStartWidth = th ? th.offsetWidth / 16 : (this.columnDefs[index].width || 2);
    this.resizing = true;
    document.body.classList.add('col-resizing');
  }

  @HostListener('document:mousemove', ['$event'])
  onResizeMove(event: MouseEvent) {
    if (!this.resizing || this.resizeSourceIndex === null) return;
    const col = this.columnDefs[this.resizeSourceIndex];
    const deltaRem = (event.clientX - this.resizeStartX) / 16;
    const min = col.minWidth ?? 2;
    const max = col.maxWidth ?? Infinity;
    col.width = Math.min(max, Math.max(min, this.resizeStartWidth + deltaRem));
  }

  @HostListener('document:mouseup')
  onResizeEnd() {
    if (!this.resizing) return;
    this.resizing = false;
    this.resizeSourceIndex = null;
    document.body.classList.remove('col-resizing');
    this.columnDefsChange.emit([...this.columnDefs]);
  }
}
